import { ConnectorNotConnectedError, getAccount, GetAccountReturnType } from '@wagmi/core'

import { CHAIN_CONFIGS } from '@/constants/chains'
import i18n from '@/i18n'
import { INetwork } from '@/types'
import { WALLET_WAGMI } from '@/utils'
import { MOCK_EVM_WALLET } from '@/utils/constants'

import { ethers } from 'ethers'
import { Config, Connector, ConnectorAlreadyConnectedError, UseAccountReturnType } from 'wagmi'
import { ConnectMutateAsync, SendTransactionMutateAsync, SignMessageMutateAsync } from 'wagmi/query'

import { BaseWallet } from './BaseWallet'
import { IWalletConnectOptions, PsbtSignOptions, TransactionOptions, wagmiConfig } from './types'

type SignMessageFn = SignMessageMutateAsync<unknown> | null
type SignTxsFn = SendTransactionMutateAsync<Config, unknown> | null
type ConnectFn = ConnectMutateAsync<Config, unknown> | null
type DisconnectFn = (exceptionId?: Connector) => void | null

type WagmiContext = {
  signMessage: SignMessageFn
  sendTransaction: SignTxsFn
  connect: ConnectFn
  disconnect: DisconnectFn
  data: UseAccountReturnType
}

export class WagmiWallet extends BaseWallet {
  name = 'WagmiWallet'
  walletId = '' // can be metamask, rabby, ... connector.id
  walletName = '' // connector.name
  key = WALLET_WAGMI
  address = ''
  onAccountChanged: IWalletConnectOptions['onAccountChanged']
  onDisconnected: IWalletConnectOptions['onDisconnected']
  silentlyConnect: (() => void) | null = null
  private status: GetAccountReturnType['status'] = 'connecting'
  private networkInfo: INetwork | undefined
  private wagmiContext = {} as WagmiContext

  async getAddress() {
    return MOCK_EVM_WALLET || this.address || ''
  }

  syncData(params: WagmiContext) {
    const { data } = params
    this.wagmiContext = params

    const newWallet = data?.address ?? ''
    const newStatus = data?.status ?? 'connecting'

    const walletChanged = this.address && newWallet && this.address.toLowerCase() !== newWallet.toLowerCase()
    const isDisconnected = this.status === 'connected' && newStatus === 'disconnected'

    this.address = newWallet
    this.status = newStatus

    if (walletChanged && this.networkInfo) {
      this.onAccountChanged?.({ address: newWallet, networkInfo: this.networkInfo })
    }

    if (isDisconnected) {
      this.onDisconnected?.(this.key)
      this.disconnect()
    }

    if (data?.connector) {
      this.walletId = data?.connector?.id ?? ''
      this.walletName = data?.connector?.name ?? ''
    }
  }

  isInstalled(): boolean {
    return true
  }

  private async getAddressFromConnector(connector: Connector) {
    return ((await connector?.getAccounts?.())?.[0] as string) || getAccount(wagmiConfig)?.address || ''
  }

  async connect(
    network: string,
    options: IWalletConnectOptions,
    needRetry = true
  ): Promise<{ address: string; networkInfo: INetwork; compressedPublicKey: string | undefined }> {
    const { onAccountChanged, onDisconnected, disconnectOthers, reconnect } = options
    const { connect, disconnect } = this.wagmiContext
    const connector = options?.adapter as Connector

    if (disconnectOthers) await disconnect?.()

    if (!connector || !connect) throw new Error('Connector is not initialized')

    this.onAccountChanged = onAccountChanged
    this.onDisconnected = onDisconnected
    this.networkInfo = options.network
    let address = ''
    try {
      const chainId = CHAIN_CONFIGS[network]?.chainId
      if (!reconnect) {
        const { accounts } = (await connect?.({ connector, chainId })) || {}
        address = accounts?.[0]
      } else {
        address = await this.getAddressFromConnector(connector)
      }

      // why not use `connector`: connector when the first connect dont have data, only have name, id, icon
      const connectorInfo = wagmiConfig.connectors?.find((e) => e.id === connector.id) as Connector
      this.silentlyConnect = async () => {
        try {
          const { accounts } = await connect({ connector: connectorInfo, chainId })
          if (accounts?.[0] && !this.address) this.address = accounts[0]
        } catch {}
      }
    } catch (error) {
      if (error instanceof ConnectorAlreadyConnectedError) {
        address = await this.getAddressFromConnector(connector)
      } else {
        console.error('wagmi connect error', error)
        throw error
      }
    }

    if (!address) {
      if (needRetry && !reconnect) {
        // sometime wagmi not return address, retry will work.
        return await this.connect(network, options, false)
      }
      throw new Error('Not found address after connect wallets')
    }
    return {
      address: MOCK_EVM_WALLET || address,
      networkInfo: options.network,
      compressedPublicKey: MOCK_EVM_WALLET || address,
    }
  }

  async disconnect(): Promise<void> {
    const { disconnect } = this.wagmiContext
    disconnect?.()
    this.address = ''
    this.silentlyConnect = null
  }

  async sendTransaction(
    toAddress: string,
    amount: number | bigint,
    network: string,
    options?: TransactionOptions
  ): Promise<string> {
    const {
      sendTransaction,
      data: { connector },
    } = this.wagmiContext
    if (!sendTransaction) throw new Error('Wallet is not connected')

    if (!connector) throw new Error('Wallet connector not found')

    const expectedChainId = +CHAIN_CONFIGS[network]?.chainId
    const currentChain = await connector.getChainId?.()
    if (currentChain !== expectedChainId) {
      const resp = await connector.switchChain?.({ chainId: expectedChainId })
      console.debug('switch chain successfully', resp)
    }

    const format = (v: string | number | undefined) =>
      v ? (typeof v === 'string' ? v : ethers.toQuantity(v)) : undefined
    const payload = {
      ...options,
      to: toAddress,
      gasLimit: options?.gasLimit ? ethers.toQuantity(options.gasLimit) : options?.gasLimit,
      value: amount ? ethers.toQuantity(amount) : amount,
      maxFeePerGas: format(options?.maxFeePerGas),
      maxPriorityFeePerGas: format(options?.maxPriorityFeePerGas),
    }
    return this.handleLostConnection(() => sendTransaction?.(payload as any))
  }

  async getWalletAddress() {
    return this.address
  }

  async handleLostConnection(func: () => Promise<any>) {
    return func().catch(async (e) => {
      if (!(e instanceof ConnectorNotConnectedError)) throw e
      console.error('wagmi lost connection, reconnecting...')
      await this.silentlyConnect?.()
      return func().catch((e) => {
        throw e instanceof ConnectorNotConnectedError ? new Error(i18n.t('error.LOST_WALLET_CONNECTION')) : e
      })
    })
  }

  async signMessage(message: string): Promise<string> {
    const { signMessage } = this.wagmiContext
    if (!signMessage) throw new Error('Wallet is not connected')
    return this.handleLostConnection(() => signMessage?.({ message }))
  }

  async getCompressedPublicKey(): Promise<string> {
    return this.address
  }

  async signPsbt(_psbt: string, _network: string, _options?: PsbtSignOptions): Promise<string> {
    throw new Error('SignPsbt is not supported for Metamask Wallet')
  }

  priority(): number {
    return 2 // unused
  }
}
